<script lang="ts">
import { inject, onMounted, reactive, defineComponent, ref } from 'vue';

import { Room } from '@/entities/Room';
import RoomServiceInterface from '@/services/interfaces/RoomServiceInterface';
import ParticipantServiceInterface from '@/services/interfaces/ParticipantServiceInterface';
import Card from './Card.vue';
import QRCodeReader from './QRCodeReader.vue';
import MessageError from './MessageError.vue';
import FooterComponent from './FooterComponent.vue';
import { Participant } from '@/entities/Participant';

export default defineComponent({
  name: 'SelectRooms',
  components: {
    Card,
    QRCodeReader,
    MessageError,
    FooterComponent,
  },
  setup() {
    const qrCode = ref('');
    const qrStreamKey = ref(0);
    const participantService =
      inject<ParticipantServiceInterface>('participantService');
    const roomService = inject<RoomServiceInterface>('roomService');

    const data = reactive({
      room: {} as Room,
      participant: {} as Participant,
      roomsList: [] as Room[] | undefined,
      messageError: '',
    });

    const onSelectRoom = () => {
      localStorage.setItem('selected_room', JSON.stringify(data.room));
    };

    const addParticipant = (code: string) => {
      participantService
        ?.addParticipant({
          reference_number: code,
          room_id: data.room?.id,
        })
        .then(response => {
          const { exists, participant } = response.data;
          data.participant = {
            exists,
            name: participant.name,
            reference_number: participant.reference_number,
          };
          data.messageError = '';
          qrCode.value = code;
        })
        .catch(error => {
          data.messageError =
            error.response.data.data.reference_number[0] || '';
        });
    };

    const resetQrStream = () => {
      qrStreamKey.value += 1;
    };

    const readQRCode = (code: string) => {
      if (code !== '') {
        setTimeout(() => {
          addParticipant(code);
          resetQrStream();
        }, 1000);
      }
    };

    const clearData = () => {
      data.participant = {} as Participant;
      data.messageError = '';
      qrCode.value = '';
    };

    onMounted(async () => {
      const selectedRoom = localStorage.getItem('selected_room');
      if (selectedRoom) data.room = JSON.parse(selectedRoom);

      await roomService
        ?.getRoomsAll()
        .then(response => (data.roomsList = response))
        .catch(error => console.error(error));
    });

    return { data, onSelectRoom, readQRCode, qrCode, clearData, qrStreamKey };
  },
});
</script>

<template>
  <Card class="bg-white w-10/12 z-10 flex flex-col p-8">
    <div class="flex">
      <img src="@/assets/tower.svg" class="w-10 mr-4" />
      <div class="relative w-full">
        <select
          v-model="data.room"
          :onchange="onSelectRoom"
          :disabled="qrCode !== ''"
          class="block w-full pl-3 pr-4 py-1 border border-blue-300 rounded-xl bg-white text-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option :value="{}" selected>Escolha uma opção</option>
          <option
            v-for="(room, index) in data.roomsList"
            :key="index"
            :value="room"
          >
            {{ room.name }}
          </option>
        </select>
        <!-- Ícone dentro do select -->
        <svg
          class="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none"
          width="15"
          height="9"
          viewBox="0 0 15 9"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.5723 0H13.1934C14.2383 0 14.7608 1.26465 14.0235 2.00195L8.21292 7.8125C7.75394 8.27148 7.01175 8.27148 6.55765 7.8125L0.742221 2.00195C0.0049161 1.26465 0.527377 0 1.5723 0Z"
            fill="#015ABB"
          />
        </svg>
      </div>
    </div>
    <QRCodeReader
      :key="qrStreamKey"
      :readQRCode="readQRCode"
      v-if="data.room.id"
    />
    <MessageError :message="data.messageError" v-if="data.room.id" />
  </Card>

  <FooterComponent
    v-if="qrCode"
    :room="data.room"
    :participant="data.participant"
    :clearData="clearData"
  />
</template>

<style>
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
</style>
